import type { ProjectionWarning } from "@shared/roi/types";
import type { EditorPanelKey } from "@client/components/editor/EditorToolbar";

/**
 * How a warning finds the field that caused it.
 *
 * `ProjectionWarning` carries `year`, `packageId` and `lineItemId` precisely so
 * the UI can do this; the ids below are the anchors the panels render
 * (`PackageCard`, `YearRow`, `ScenarioSettingsPanel`), and this module is the
 * one place that spells them, so a renamed anchor breaks in one file.
 */

/** Stable across recomputations, so a dismissal survives the next keystroke. */
export function warningKey(warning: ProjectionWarning): string {
  return [warning.code, warning.year ?? "", warning.packageId ?? "", warning.lineItemId ?? ""].join(
    ":",
  );
}

/** The element a warning is about, or `null` when it is about the whole model. */
export function warningTarget(warning: ProjectionWarning): string | null {
  if (warning.packageId) return `package-${warning.packageId}`;
  if (warning.year !== undefined) return `year-${warning.year}`;
  if (warning.code === "EMPTY_HORIZON" || warning.code === "NEGATIVE_FOREVER") {
    return "scenario-settings";
  }
  return null;
}

/**
 * Which of the rail's dialogs holds the field a warning is about.
 *
 * Since the inputs moved behind the rail, the anchor `warningTarget` names is
 * not in the document until its dialog is open — so "Go to it" has to open the
 * right one first. This is the second half of the same mapping and lives beside
 * it so the two cannot drift.
 */
export function warningPanel(warning: ProjectionWarning): EditorPanelKey | null {
  if (warning.packageId) return "packages";
  if (warning.lineItemId) return "lines";
  if (warning.year !== undefined) return "adoption";
  if (warning.code === "EMPTY_HORIZON" || warning.code === "NEGATIVE_FOREVER") {
    return "settings";
  }
  return null;
}
